import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Wine, Loader2, AlertCircle, RefreshCw, Clock, Filter, X, CheckSquare, Search, Hash, Check } from 'lucide-react';
import { MasterProductControllerApi, MasterProductVintageSku, MasterProductVintageSkuListing, MasterProductFilterValues, MasterProductVintageCriticReview } from '../api';
import { apiConfiguration } from "../api/apiConfig";
import { CriticReviewData, formatPercentage } from './wine/wineFormatting';
import LwinChain from './LwinChain';
import { color, font, radius, shadow } from '../theme';
import { ReferencePriceGrid, PriceComparisons, CriticReviewsList, CriticReviewModal } from './wine/WineMetrics';

// 1000 is BBR's non-vintage sentinel (NV wines + bundles); show "NV" rather than the raw number.
const NV_VINTAGE = 1000;
const formatVintage = (v?: number | null): string =>
    v === NV_VINTAGE ? 'NV' : (v != null ? String(v) : '');

// Maturity enum → friendly text; UNKNOWN / missing reads as "—" (BBR simply has no maturity, e.g. bundles).
const formatMaturity = (m?: string | null): string => {
    if (!m || m === 'UNKNOWN') return '—';
    return m.toLowerCase().split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

// Tucks noisy technical IDs (SKU UID, canonical code, LWIN16, listing IDs) behind a small icon.
// Hover shows them (native tooltip); click copies them to the clipboard.
const CopyableIds = ({ ids }: { ids: { label: string; value?: string | number | null }[] }) => {
    const items = ids.filter(i => i.value != null && i.value !== '');
    const [copied, setCopied] = useState(false);
    if (!items.length) return null;
    const text = items.map(i => `${i.label}: ${i.value}`).join('\n');
    return (
        <button
            type="button"
            title={text}
            aria-label="Copy identifiers"
            onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard?.writeText(text)
                    .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200); })
                    .catch(() => {});
            }}
            style={{
                border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px',
                color: copied ? 'var(--success)' : 'var(--ink-400)',
                display: 'inline-flex', alignItems: 'center', lineHeight: 0,
            }}
        >
            {copied ? <Check size={13} /> : <Hash size={13} />}
        </button>
    );
};

interface MasterProductsProps {
    refreshKey?: number;
    onRefresh?: () => void;
}

// Friendly merchant name for a source code (used on per-listing source badges + the source filter).
const SOURCE_LABELS: Record<string, string> = { BBX: 'BBR', LW_TRADING: 'L&W' };
const sourceLabel = (code?: string | null) => (code ? (SOURCE_LABELS[code] ?? code) : 'Source');

// Per-source chip styling so BBR and L&W listings are instantly distinguishable in a mixed list.
const sourceBadgeStyle = (code?: string | null): React.CSSProperties => {
    const isBbr = code === 'BBX';
    return {
        fontSize: '11px',
        fontWeight: 600,
        padding: '1px 6px',
        borderRadius: '4px',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
        color: isBbr ? 'var(--claret-700)' : '#1e6b6b',
        backgroundColor: isBbr ? 'rgba(125, 30, 40, 0.08)' : 'rgba(30, 107, 107, 0.10)',
        border: `1px solid ${isBbr ? 'rgba(125, 30, 40, 0.25)' : 'rgba(30, 107, 107, 0.30)'}`,
    };
};

const MasterProducts = ({ refreshKey, onRefresh }: MasterProductsProps) => {
    // Each row is now a VINTAGE (flattened), carrying a lightweight `product` summary.
    const [vintages, setVintages] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Pagination state
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

    // Deep-link / exact-id focus: ?vintageId=<uuid> pins the view to a single vintage (e.g. clicked
    // from an alert row). The backend short-circuits to that one vintage, ignoring every other filter.
    const [searchParams, setSearchParams] = useSearchParams();
    const focusVintageId = searchParams.get('vintageId') || '';
    const [draftVintageId, setDraftVintageId] = useState('');
    const setFocusVintageId = (id: string) => {
        const next = new URLSearchParams(searchParams);
        if (id.trim()) next.set('vintageId', id.trim()); else next.delete('vintageId');
        setSearchParams(next, { replace: true });
    };

    // Filter state - set filterOpen to true by default
    const [filterOpen, setFilterOpen] = useState(true);
    const [name, setName] = useState('');
    const [producer, setProducer] = useState('');
    const [country, setCountry] = useState('');
    const [region, setRegion] = useState('');
    const [subRegion, setSubRegion] = useState('');
    const [colour, setColour] = useState('');
    const [category, setCategory] = useState('');
    const [sourceCodes, setSourceCodes] = useState<string[]>([]);
    const [hasLwin, setHasLwin] = useState<boolean | undefined>(undefined);
    const [modifiedAfter, setModifiedAfter] = useState('');
    const [modifiedBefore, setModifiedBefore] = useState('');
    const [maturity, setMaturity] = useState('');
    const [drinkingFromYear, setDrinkingFromYear] = useState<number | undefined>(undefined);
    const [drinkingToYear, setDrinkingToYear] = useState<number | undefined>(undefined);

    // Critic review filter state
    const [hasCriticReviews, setHasCriticReviews] = useState<boolean | undefined>(undefined);
    const [criticScoreMin, setCriticScoreMin] = useState<number | undefined>(undefined);
    const [criticScoreMax, setCriticScoreMax] = useState<number | undefined>(undefined);

    // New price percentage filter state
    const [priceToMarketPercentageMin, setPriceToMarketPercentageMin] = useState<number | undefined>(undefined);
    const [priceToMarketPercentageMax, setPriceToMarketPercentageMax] = useState<number | undefined>(undefined);
    const [priceToWineSearcherPercentageMin, setPriceToWineSearcherPercentageMin] = useState<number | undefined>(undefined);
    const [priceToWineSearcherPercentageMax, setPriceToWineSearcherPercentageMax] = useState<number | undefined>(undefined);
    const [priceToLastTransactionPercentageMin, setPriceToLastTransactionPercentageMin] = useState<number | undefined>(undefined);
    const [priceToLastTransactionPercentageMax, setPriceToLastTransactionPercentageMax] = useState<number | undefined>(undefined);
    const [highestBidToMarketPercentageMin, setPriceToHighestBidPercentageMin] = useState<number | undefined>(undefined);
    const [highestBidToMarketPercentageMax, setPriceToHighestBidPercentageMax] = useState<number | undefined>(undefined);

    // Draft values for all filters
    const [draftName, setDraftName] = useState('');
    const [draftProducer, setDraftProducer] = useState('');
    const [draftCountry, setDraftCountry] = useState('');
    const [draftRegion, setDraftRegion] = useState('');
    const [draftSubRegion, setDraftSubRegion] = useState('');
    const [draftColour, setDraftColour] = useState('');
    const [draftCategory, setDraftCategory] = useState('');
    const [draftSourceCodes, setDraftSourceCodes] = useState<string[]>([]);
    const [draftHasLwin, setDraftHasLwin] = useState<boolean | undefined>(undefined);
    const [draftModifiedAfter, setDraftModifiedAfter] = useState('');
    const [draftModifiedBefore, setDraftModifiedBefore] = useState('');
    const [draftMaturity, setDraftMaturity] = useState('');
    const [draftDrinkingFromYear, setDraftDrinkingFromYear] = useState<number | undefined>(undefined);
    const [draftDrinkingToYear, setDraftDrinkingToYear] = useState<number | undefined>(undefined);
    const [draftHasCriticReviews, setDraftHasCriticReviews] = useState<boolean | undefined>(undefined);
    const [draftCriticScoreMin, setDraftCriticScoreMin] = useState<number | undefined>(undefined);
    const [draftCriticScoreMax, setDraftCriticScoreMax] = useState<number | undefined>(undefined);
    const [draftPriceToMarketPercentageMin, setDraftPriceToMarketPercentageMin] = useState<number | undefined>(undefined);
    const [draftPriceToMarketPercentageMax, setDraftPriceToMarketPercentageMax] = useState<number | undefined>(undefined);
    const [draftPriceToWineSearcherPercentageMin, setDraftPriceToWineSearcherPercentageMin] = useState<number | undefined>(undefined);
    const [draftPriceToWineSearcherPercentageMax, setDraftPriceToWineSearcherPercentageMax] = useState<number | undefined>(undefined);
    const [draftPriceToLastTransactionPercentageMin, setDraftPriceToLastTransactionPercentageMin] = useState<number | undefined>(undefined);
    const [draftPriceToLastTransactionPercentageMax, setDraftPriceToLastTransactionPercentageMax] = useState<number | undefined>(undefined);
    const [draftPriceToHighestBidPercentageMin, setDraftPriceToHighestBidPercentageMin] = useState<number | undefined>(undefined);
    const [draftPriceToHighestBidPercentageMax, setDraftPriceToHighestBidPercentageMax] = useState<number | undefined>(undefined);

    // Filter options from API
    const [filterOptions, setFilterOptions] = useState<MasterProductFilterValues>({
        categories: [],
        colours: [],
        countries: [],
        regions: [],
        subRegions: [],
        producers: []
    });
    const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(true);

    // Unique values for fallback
    const [uniqueCategories, setUniqueCategories] = useState<string[]>([]);
    const [uniqueRegions, setUniqueRegions] = useState<string[]>([]);
    const [uniqueSubRegions, setUniqueSubRegions] = useState<string[]>([]);
    const [uniqueColours, setUniqueColours] = useState<string[]>([]);
    const [uniqueCountries, setUniqueCountries] = useState<string[]>([]);
    const [uniqueProducers, setUniqueProducers] = useState<string[]>([]);

    // Debounce timer reference
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

    // Add collapsible section state - all collapsed by default
    const [collapsedSections, setCollapsedSections] = useState({
        textSearch: true,
        wineAttributes: true,
        origins: true,
        specialFilters: true,
        vintageFilters: true,
        criticReviews: true,
        dateFilters: true,
        priceComparison: true
    });

    // Add modal state for critic reviews
    const [selectedReview, setSelectedReview] = useState<CriticReviewData | null>(null);

    // Cellar status from BBR account (keyed by masterSkuId)
    const [skuStatus, setSkuStatus] = useState<Record<string, any>>({});

    // Filter styles object (same as ProductClaude)
    const filterStyles = {
        sidebar: {
            width: '320px',
            flexShrink: 0,
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            height: '80vh',
            position: 'sticky' as const,
            top: '24px',
            display: 'flex',
            flexDirection: 'column' as const,
            overflow: 'hidden',
        },
        filterPanel: {
            padding: '24px',
            overflowY: 'scroll' as const,
            flex: 1,
            height: '100%',
            scrollbarWidth: 'thin' as const,
            scrollbarColor: 'var(--ink-400) var(--hover)',
        },
        filterSectionTitle: {
            fontSize: '16px',
            fontWeight: '600' as const,
            color: 'var(--ink-900)',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer' as const,
            padding: '8px 0',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
        },
        filterSectionTitleHover: {
            backgroundColor: 'var(--hover)',
        },
        filterSectionContent: {
            overflow: 'hidden' as const,
            transition: 'max-height 0.3s ease-out',
        },
        filterSectionCollapsed: {
            maxHeight: '0px',
        },
        filterSectionExpanded: {
            maxHeight: '1000px',
        },
        filterGroup: {
            marginBottom: '16px',
        },
        filterLabel: {
            display: 'block',
            fontSize: '14px',
            fontWeight: '500' as const,
            color: 'var(--ink-700)',
            marginBottom: '6px',
        },
        filterInput: {
            width: '100%',
            padding: '8px 12px',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            fontSize: '14px',
            color: 'var(--ink-900)',
            backgroundColor: 'var(--surface)',
        },
        filterSelect: {
            width: '100%',
            padding: '8px 12px',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            fontSize: '14px',
            color: 'var(--ink-900)',
            backgroundColor: 'var(--surface)',
            cursor: 'pointer' as const,
        },
        checkboxGroup: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '8px',
        },
        checkbox: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            color: 'var(--ink-700)',
            cursor: 'pointer' as const,
            padding: '4px',
            borderRadius: '4px',
            transition: 'background-color 0.2s',
        },
        checkboxChecked: {
            backgroundColor: 'var(--info-bg)',
            color: 'var(--info)',
        },
        filterActions: {
            display: 'flex',
            gap: '8px',
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: '1px solid var(--border)',
        },
        applyButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: 'var(--claret-600)',
            color: 'white',
            border: '1px solid var(--claret-600)',
            borderRadius: '6px',
            cursor: 'pointer' as const,
            fontSize: '14px',
            fontWeight: '500' as const,
            transition: 'background-color 0.2s',
        },
        applyButtonHover: {
            backgroundColor: 'var(--claret-700)',
        },
        filterClearButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: 'var(--border)',
            color: 'var(--ink-700)',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            cursor: 'pointer' as const,
            fontSize: '14px',
            fontWeight: '500' as const,
        },
        filterCount: {
            backgroundColor: 'var(--danger)',
            color: 'white',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: '600' as const,
        },
    };

    // Enhanced styles object with detailed pricing and listing styles
    const styles = {
        container: {
            maxWidth: '1680px',
            margin: '0 auto',
            padding: '24px 32px',
            backgroundColor: color.canvas,
            minHeight: '100vh',
        },
        contentWrapper: {
            display: 'flex',
            gap: '24px',
            width: '100%',
        },
        mainContent: {
            flex: 1,
            minWidth: 0,
        },
        loadingContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            color: 'var(--ink-500)',
        },
        errorContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            backgroundColor: 'var(--danger-bg)',
            border: '1px solid var(--danger-border)',
            borderRadius: '12px',
            padding: '32px',
        },
        errorContent: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px',
        },
        errorText: {
            color: 'var(--danger)',
            fontSize: '16px',
            fontWeight: '500' as const,
        },
        retryButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: 'var(--danger)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer' as const,
            fontSize: '14px',
            fontWeight: '500' as const,
        },
        bbrBadge: {
            backgroundColor: '#FBF3E3',
            color: color.gold,
            border: '1px solid #F0E2C5',
            padding: '2px 8px',
            borderRadius: radius.pill,
            fontSize: '11.5px',
            fontWeight: '600' as const,
        },
        lwinBadge: {
            backgroundColor: color.claret[50],
            color: color.claret[700],
            border: `1px solid ${color.claret[100]}`,
            padding: '2px 8px',
            borderRadius: radius.pill,
            fontSize: '11.5px',
            fontWeight: '600' as const,
        },
        // Each vintage is now a first-class card (flattened out of the product 1:many).
        // Each vintage is a distinct white block separated by a border + gap. Internal sections are
        // transparent so the surface reads uniform edge to edge.
        vintageCard: {
            backgroundColor: color.surface,
            border: `1px solid ${color.borderStrong}`,
            borderRadius: radius.lg,
            marginBottom: '16px',
            overflow: 'hidden' as const,
        },
        vintageHeader: {
            padding: '20px 24px',
            position: 'relative' as const,
            borderBottom: `1px solid ${color.border}`,
        },
        vintageContent: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
        },
        vintageLeft: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
        },
        vintageTitle: {
            fontFamily: font.serif,
            fontSize: '17px',
            fontWeight: '600' as const,
            color: color.ink[900],
            letterSpacing: '0.005em',
            margin: 0,
            textDecoration: 'none',
        },
        pagination: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '32px',
            padding: '16px 0',
        },
        pageButton: {
            padding: '8px 16px',
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            cursor: 'pointer' as const,
            fontSize: '14px',
            fontWeight: '500' as const,
            color: 'var(--ink-700)',
        },
        pageInfo: {
            fontSize: '14px',
            color: 'var(--ink-500)',
        },
        expertReviewInline: {
            fontSize: '13px',
            color: 'var(--ink-500)',
            marginTop: '4px',
            lineHeight: '1.4',
            maxWidth: '600px',
        },
        criticReviewsContainer: {
            marginTop: '8px',
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '4px',
        },
        criticReviewItem: {
            fontSize: '13px',
            color: 'var(--claret-600)',
            cursor: 'pointer' as const,
            textDecoration: 'underline',
            lineHeight: '1.5',
        },
        modalOverlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
        },
        modalContent: {
            backgroundColor: 'var(--surface)',
            borderRadius: '12px',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflow: 'auto',
            padding: '24px',
            position: 'relative' as const,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
        modalHeader: {
            fontSize: '18px',
            fontWeight: '600' as const,
            color: 'var(--ink-900)',
            marginBottom: '16px',
            paddingRight: '32px',
        },
        modalBody: {
            fontSize: '14px',
            color: 'var(--ink-700)',
            lineHeight: '1.6',
        },
        modalCloseButton: {
            position: 'absolute' as const,
            top: '16px',
            right: '16px',
            width: '32px',
            height: '32px',
            border: 'none',
            backgroundColor: 'var(--border)',
            borderRadius: '6px',
            cursor: 'pointer' as const,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ink-500)',
            fontSize: '20px',
        },
        listingsContainer: {
            backgroundColor: 'transparent',
        },
        // SKU is no longer a detached bordered box — it flows inside the vintage card as one group.
        // A hairline divider between consecutive SKUs is added in the render (skuIndex > 0 only).
        skuContainer: {
        },
        skuHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 20px',
            borderBottom: `1px solid ${color.border}`,
        },
        skuTitle: {
            fontSize: '16px',
            fontWeight: '600' as const,
            color: 'var(--ink-900)',
            margin: 0,
        },
        pricingGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '16px',
            minWidth: '600px',
        },
        pricingItem: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            textAlign: 'center' as const,
        },
        pricingLabel: {
            fontSize: '11px',
            color: 'var(--ink-500)',
            fontWeight: '500' as const,
            marginBottom: '4px',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px',
        },
        pricingValue: {
            fontSize: '13px',
            fontWeight: '600' as const,
            color: 'var(--ink-900)',
            lineHeight: '1.2',
        },
        listingsSection: {
            backgroundColor: 'transparent',
        },
        // Columnar listing grid (BBR-style): every row shares these column tracks so
        // price / stock / availability / comparisons / ids line up vertically across rows.
        listingHeaderRow: {
            display: 'grid',
            gridTemplateColumns: '150px 160px 120px minmax(260px, 1fr) auto',
            alignItems: 'center',
            columnGap: '16px',
            padding: '7px 20px',
            borderBottom: `1px solid ${color.border}`,
        },
        listingColLabel: {
            fontSize: '10px',
            fontWeight: '600' as const,
            color: 'var(--ink-500)',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px',
        },
        listingRow: {
            display: 'grid',
            gridTemplateColumns: '150px 160px 120px minmax(260px, 1fr) auto',
            alignItems: 'center',
            columnGap: '16px',
            padding: '10px 20px',
            borderBottom: `1px solid ${color.border}`,
            transition: 'background-color 0.15s ease',
        },
        listingCell: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '2px',
            minWidth: 0,
        },
        listingPrice: {
            fontSize: '15px',
            fontWeight: '700' as const,
            color: color.ink[900],
        },
        priceComparisonGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '8px',
            marginTop: '6px',
            padding: '8px',
            backgroundColor: 'var(--canvas)',
            borderRadius: '4px',
            border: '1px solid var(--border)',
        },
        priceComparisonItem: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'flex-start',
            gap: '2px',
        },
        priceComparisonLabel: {
            fontSize: '10px',
            color: 'var(--ink-500)',
            fontWeight: '500' as const,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px',
        },
        priceComparisonValue: {
            fontSize: '12px',
            fontWeight: '600' as const,
        },
    };

    // Pricing/formatting helpers now live in ./wine/wineFormatting (shared with the Alerts page).
    // formatPercentage is imported above; getPriceIndicator / formatPriceWithDate are used inside
    // the shared <PriceComparisons> / <ReferencePriceGrid> components.

    // Load filter options once when component mounts
    useEffect(() => {
        const api = new MasterProductControllerApi(apiConfiguration);
        setIsLoadingFilterOptions(true);

        api.getMasterProductFilters()
            .then(({ data }) => {
                setFilterOptions(data);
            })
            .catch(err => {
                console.error('Failed to load filter options:', err);
            })
            .finally(() => {
                setIsLoadingFilterOptions(false);
            });
    }, []);

    // Load cellar SKU status (non-critical, fails silently)
    useEffect(() => {
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        fetch(`${apiBase}/api/bbr-account/sku-status`)
            .then(res => res.ok ? res.json() : {})
            .then(data => setSkuStatus(data || {}))
            .catch(() => {});
    }, []);

    // Load VINTAGES from API (flattened, vintage-level pagination).
    // Raw fetch (like the sku-status call) so we don't need to regenerate the OpenAPI client.
    useEffect(() => {
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('size', String(size));
        const add = (k: string, v: unknown) => {
            if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
        };
        // When a single vintage is pinned, the backend ignores all other params — so we needn't strip them.
        add('vintageId', focusVintageId);
        add('name', name); add('producer', producer); add('country', country); add('region', region);
        add('subRegion', subRegion); add('colour', colour); add('category', category);
        sourceCodes.forEach(c => params.append('sourceCodes', c));
        add('hasLwin', hasLwin);
        add('modifiedAfter', modifiedAfter); add('modifiedBefore', modifiedBefore);
        add('priceToMarketPercentageMin', priceToMarketPercentageMin);
        add('priceToMarketPercentageMax', priceToMarketPercentageMax);
        add('priceToWineSearcherPercentageMin', priceToWineSearcherPercentageMin);
        add('priceToWineSearcherPercentageMax', priceToWineSearcherPercentageMax);
        add('priceToLastTransactionPercentageMin', priceToLastTransactionPercentageMin);
        add('priceToLastTransactionPercentageMax', priceToLastTransactionPercentageMax);
        add('highestBidToMarketPercentageMin', highestBidToMarketPercentageMin);
        add('highestBidToMarketPercentageMax', highestBidToMarketPercentageMax);
        add('maturity', maturity); add('drinkingFromYear', drinkingFromYear); add('drinkingToYear', drinkingToYear);
        add('hasCriticReviews', hasCriticReviews);
        add('criticScoreMin', criticScoreMin); add('criticScoreMax', criticScoreMax);

        setLoading(true);
        setError(null);

        fetch(`${apiBase}/api/master-products/vintages?${params.toString()}`)
            .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
            .then(data => {
                const content: any[] = data.content ?? [];
                setVintages(content);
                setTotalPages(data.totalPages ?? 1);

                // Derive filter dropdown options from the product summary on each vintage row.
                if (content.length && !filterOptions.categories?.length) {
                    const uniq = (prop: string) => {
                        const values: string[] = [];
                        content.forEach(item => {
                            const val = item.product?.[prop];
                            if (val && !values.includes(val)) values.push(val);
                        });
                        return values.sort();
                    };
                    setUniqueCategories(uniq('category'));
                    setUniqueRegions(uniq('region'));
                    setUniqueSubRegions(uniq('subRegion'));
                    setUniqueColours(uniq('colour'));
                    setUniqueCountries(uniq('country'));
                    setUniqueProducers(uniq('producer'));
                }
            })
            .catch(err => {
                setError('Failed to load master products');
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [page, size, refreshKey, focusVintageId, name, producer, country, region, subRegion, colour, category, sourceCodes, hasLwin, modifiedAfter, modifiedBefore, priceToMarketPercentageMin, priceToMarketPercentageMax, priceToWineSearcherPercentageMin, priceToWineSearcherPercentageMax, priceToLastTransactionPercentageMin, priceToLastTransactionPercentageMax, highestBidToMarketPercentageMin, highestBidToMarketPercentageMax, maturity, drinkingFromYear, drinkingToYear, hasCriticReviews, criticScoreMin, criticScoreMax, filterOptions.categories?.length]);

    // Keep the draft input in step with the URL, and snap back to page 0 whenever the pinned vintage changes.
    useEffect(() => {
        setDraftVintageId(focusVintageId);
        setPage(0);
    }, [focusVintageId]);

    // Initialize draft values when filter values change from external sources
    useEffect(() => {
        setDraftName(name);
        setDraftProducer(producer);
        setDraftCategory(category);
        setDraftRegion(region);
        setDraftSubRegion(subRegion);
        setDraftColour(colour);
        setDraftSourceCodes(sourceCodes);
        setDraftCountry(country);
        setDraftHasLwin(hasLwin);
        setDraftModifiedAfter(modifiedAfter);
        setDraftModifiedBefore(modifiedBefore);
        setDraftMaturity(maturity);
        setDraftDrinkingFromYear(drinkingFromYear);
        setDraftDrinkingToYear(drinkingToYear);
        setDraftHasCriticReviews(hasCriticReviews);
        setDraftCriticScoreMin(criticScoreMin);
        setDraftCriticScoreMax(criticScoreMax);
        setDraftPriceToMarketPercentageMin(priceToMarketPercentageMin);
        setDraftPriceToMarketPercentageMax(priceToMarketPercentageMax);
        setDraftPriceToWineSearcherPercentageMin(priceToWineSearcherPercentageMin);
        setDraftPriceToWineSearcherPercentageMax(priceToWineSearcherPercentageMax);
        setDraftPriceToLastTransactionPercentageMin(priceToLastTransactionPercentageMin);
        setDraftPriceToLastTransactionPercentageMax(priceToLastTransactionPercentageMax);
        setDraftPriceToHighestBidPercentageMin(highestBidToMarketPercentageMin);
        setDraftPriceToHighestBidPercentageMax(highestBidToMarketPercentageMax);
    }, [name, producer, category, region, subRegion, colour, sourceCodes, country, hasLwin, modifiedAfter, modifiedBefore, maturity, drinkingFromYear, drinkingToYear, hasCriticReviews, criticScoreMin, criticScoreMax, priceToMarketPercentageMin, priceToMarketPercentageMax, priceToWineSearcherPercentageMin, priceToWineSearcherPercentageMax, priceToLastTransactionPercentageMin, priceToLastTransactionPercentageMax, highestBidToMarketPercentageMin, highestBidToMarketPercentageMax]);

    // Toggle collapsible sections
    const toggleSection = (section: keyof typeof collapsedSections) => {
        setCollapsedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Apply all filters - triggered by button click
    const applyAllFilters = () => {
        setFocusVintageId(draftVintageId);
        setName(draftName);
        setProducer(draftProducer);
        setCategory(draftCategory);
        setRegion(draftRegion);
        setSubRegion(draftSubRegion);
        setColour(draftColour);
        setSourceCodes(draftSourceCodes);
        setCountry(draftCountry);
        setHasLwin(draftHasLwin);
        setModifiedAfter(draftModifiedAfter);
        setModifiedBefore(draftModifiedBefore);
        setMaturity(draftMaturity);
        setDrinkingFromYear(draftDrinkingFromYear);
        setDrinkingToYear(draftDrinkingToYear);
        setHasCriticReviews(draftHasCriticReviews);
        setCriticScoreMin(draftCriticScoreMin);
        setCriticScoreMax(draftCriticScoreMax);
        setPriceToMarketPercentageMin(draftPriceToMarketPercentageMin);
        setPriceToMarketPercentageMax(draftPriceToMarketPercentageMax);
        setPriceToWineSearcherPercentageMin(draftPriceToWineSearcherPercentageMin);
        setPriceToWineSearcherPercentageMax(draftPriceToWineSearcherPercentageMax);
        setPriceToLastTransactionPercentageMin(draftPriceToLastTransactionPercentageMin);
        setPriceToLastTransactionPercentageMax(draftPriceToLastTransactionPercentageMax);
        setPriceToHighestBidPercentageMin(draftPriceToHighestBidPercentageMin);
        setPriceToHighestBidPercentageMax(draftPriceToHighestBidPercentageMax);
        setPage(0);
    };

    const clearFilters = () => {
        // Clear all filters including draft values
        setDraftVintageId('');
        setFocusVintageId('');
        setDraftName('');
        setDraftProducer('');
        setDraftCategory('');
        setDraftRegion('');
        setDraftSubRegion('');
        setDraftColour('');
        setDraftSourceCodes([]);
        setDraftCountry('');
        setDraftHasLwin(undefined);
        setDraftModifiedAfter('');
        setDraftModifiedBefore('');
        setDraftMaturity('');
        setDraftDrinkingFromYear(undefined);
        setDraftDrinkingToYear(undefined);
        setDraftHasCriticReviews(undefined);
        setDraftCriticScoreMin(undefined);
        setDraftCriticScoreMax(undefined);
        setDraftPriceToMarketPercentageMin(undefined);
        setDraftPriceToMarketPercentageMax(undefined);
        setDraftPriceToWineSearcherPercentageMin(undefined);
        setDraftPriceToWineSearcherPercentageMax(undefined);
        setDraftPriceToLastTransactionPercentageMin(undefined);
        setDraftPriceToLastTransactionPercentageMax(undefined);
        setDraftPriceToHighestBidPercentageMin(undefined);
        setDraftPriceToHighestBidPercentageMax(undefined);

        setName('');
        setProducer('');
        setCategory('');
        setRegion('');
        setSubRegion('');
        setColour('');
        setSourceCodes([]);
        setCountry('');
        setHasLwin(undefined);
        setModifiedAfter('');
        setModifiedBefore('');
        setMaturity('');
        setDrinkingFromYear(undefined);
        setDrinkingToYear(undefined);
        setHasCriticReviews(undefined);
        setCriticScoreMin(undefined);
        setCriticScoreMax(undefined);

        // Clear price percentage filters
        setPriceToMarketPercentageMin(undefined);
        setPriceToMarketPercentageMax(undefined);
        setPriceToWineSearcherPercentageMin(undefined);
        setPriceToWineSearcherPercentageMax(undefined);
        setPriceToLastTransactionPercentageMin(undefined);
        setPriceToLastTransactionPercentageMax(undefined);
        setPriceToHighestBidPercentageMin(undefined);
        setPriceToHighestBidPercentageMax(undefined);

        setPage(0);
    };

    const refreshData = () => {
        if (onRefresh) {
            onRefresh();
        }
    };

    const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return 'N/A';
        try {
            return new Date(dateStr).toLocaleDateString('en-GB');
        } catch {
            return 'N/A';
        }
    };

    const getFilterOptions = (type: keyof MasterProductFilterValues, fallbackArray: string[]) => {
        const options = filterOptions[type];
        return options && options.length > 0 ? options : fallbackArray;
    };

    const getWineIconColor = (colour: string) => {
        switch (colour?.toLowerCase()) {
            case 'red': return 'var(--danger)';
            case 'white': return '#fbbf24';
            case 'rosé': case 'rose': return '#ec4899';
            case 'sparkling': return '#06b6d4';
            default: return 'var(--ink-500)';
        }
    };

    // Calculate active filter count
    const activeFilterCount = [
        name, producer, category, region, subRegion, colour, country,
        hasLwin !== undefined ? 'hasLwin' : '',
        modifiedAfter, modifiedBefore,
        maturity,
        drinkingFromYear !== undefined ? 'drinkingFromYear' : '',
        drinkingToYear !== undefined ? 'drinkingToYear' : '',
        hasCriticReviews !== undefined ? 'hasCriticReviews' : '',
        criticScoreMin !== undefined ? 'criticScoreMin' : '',
        criticScoreMax !== undefined ? 'criticScoreMax' : '',
        priceToMarketPercentageMin !== undefined ? 'priceToMarketPercentageMin' : '',
        priceToMarketPercentageMax !== undefined ? 'priceToMarketPercentageMax' : '',
        priceToWineSearcherPercentageMin !== undefined ? 'priceToWineSearcherPercentageMin' : '',
        priceToWineSearcherPercentageMax !== undefined ? 'priceToWineSearcherPercentageMax' : '',
        priceToLastTransactionPercentageMin !== undefined ? 'priceToLastTransactionPercentageMin' : '',
        priceToLastTransactionPercentageMax !== undefined ? 'priceToLastTransactionPercentageMax' : '',
        highestBidToMarketPercentageMin !== undefined ? 'highestBidToMarketPercentageMin' : '',
        highestBidToMarketPercentageMax !== undefined ? 'highestBidToMarketPercentageMax' : ''
    ].filter(Boolean).length;

    // Loading state
    if (loading && !vintages.length) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <Loader2 size={32} className="animate-spin" color="var(--ink-900)" />
                    <p style={{marginTop: '16px', fontSize: '16px'}}>Loading master products...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.errorContainer}>
                    <div style={styles.errorContent}>
                        <AlertCircle size={20} color="var(--danger)" />
                        <span style={styles.errorText}>{error}</span>
                    </div>
                    <button
                        style={styles.retryButton}
                        onClick={refreshData}
                    >
                        <RefreshCw size={16} />
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.contentWrapper}>
                {/* Sidebar Filter Panel - always visible */}
                <div style={filterStyles.sidebar}>
                    <div style={filterStyles.filterPanel}>
                        <div style={filterStyles.filterSectionTitle}>
                            Master Product Filters
                            {activeFilterCount > 0 && (
                                <span style={{ ...filterStyles.filterCount, marginLeft: '8px' }}>{activeFilterCount}</span>
                            )}
                        </div>

                        {/* Text Search Filters */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            ...(collapsedSections.textSearch ? {} : filterStyles.filterSectionTitleHover)
                        }}
                        onClick={() => toggleSection('textSearch')}>
                            <span style={{ flex: 1 }}>Text Search</span>
                            {collapsedSections.textSearch ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.textSearch ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Vintage ID</label>
                                <input
                                    type="text"
                                    value={draftVintageId}
                                    onChange={(e) => setDraftVintageId(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') applyAllFilters(); }}
                                    placeholder="Exact vintage UUID — overrides other filters"
                                    style={filterStyles.filterInput}
                                />
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Product Name</label>
                                <input
                                    type="text"
                                    value={draftName}
                                    onChange={(e) => setDraftName(e.target.value)}
                                    placeholder="Search by name"
                                    style={filterStyles.filterInput}
                                />
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Producer</label>
                                <input
                                    type="text"
                                    value={draftProducer}
                                    onChange={(e) => setDraftProducer(e.target.value)}
                                    placeholder="Enter producer"
                                    style={filterStyles.filterInput}
                                />
                            </div>
                        </div>

                        {/* Category and Type Filters */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            marginTop: '16px',
                            marginBottom: '12px'
                        }}
                        onClick={() => toggleSection('wineAttributes')}>
                            <span style={{ flex: 1 }}>Wine Attributes</span>
                            {collapsedSections.wineAttributes ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.wineAttributes ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Wine Category</label>
                                <select
                                    value={draftCategory}
                                    onChange={(e) => setDraftCategory(e.target.value)}
                                    style={filterStyles.filterSelect}
                                    disabled={isLoadingFilterOptions}
                                >
                                    <option value="">All Categories</option>
                                    {isLoadingFilterOptions ? (
                                        <option disabled>Loading categories...</option>
                                    ) : (
                                        getFilterOptions('categories', uniqueCategories).map(category => (
                                            <option key={category} value={category}>{category}</option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Wine Colour</label>
                                <select
                                    value={draftColour}
                                    onChange={(e) => setDraftColour(e.target.value)}
                                    style={filterStyles.filterSelect}
                                    disabled={isLoadingFilterOptions}
                                >
                                    <option value="">All Colours</option>
                                    {isLoadingFilterOptions ? (
                                        <option disabled>Loading colours...</option>
                                    ) : (
                                        getFilterOptions('colours', uniqueColours).map(colour => (
                                            <option key={colour} value={colour}>{colour}</option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Source (has a listing)</label>
                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', paddingTop: '4px' }}>
                                    {[{ code: 'BBX', label: 'BBR' }, { code: 'LW_TRADING', label: 'L&W' }].map(s => (
                                        <label key={s.code} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', color: 'var(--ink-700)' }}>
                                            <input
                                                type="checkbox"
                                                checked={draftSourceCodes.includes(s.code)}
                                                onChange={(e) => setDraftSourceCodes(prev =>
                                                    e.target.checked ? [...prev, s.code] : prev.filter(c => c !== s.code))}
                                            />
                                            {s.label}
                                        </label>
                                    ))}
                                </div>
                                <div style={{ fontSize: '11px', color: 'var(--ink-500)', marginTop: '2px' }}>
                                    None selected = all sources
                                </div>
                            </div>
                        </div>

                        {/* Location Filters */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            marginTop: '16px',
                            marginBottom: '12px'
                        }}
                        onClick={() => toggleSection('origins')}>
                            <span style={{ flex: 1 }}>Origins</span>
                            {collapsedSections.origins ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.origins ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Country</label>
                                <select
                                    value={draftCountry}
                                    onChange={(e) => setDraftCountry(e.target.value)}
                                    style={filterStyles.filterSelect}
                                    disabled={isLoadingFilterOptions}
                                >
                                    <option value="">All Countries</option>
                                    {isLoadingFilterOptions ? (
                                        <option disabled>Loading countries...</option>
                                    ) : (
                                        getFilterOptions('countries', uniqueCountries).map(country => (
                                            <option key={country} value={country}>{country}</option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Region</label>
                                <select
                                    value={draftRegion}
                                    onChange={(e) => setDraftRegion(e.target.value)}
                                    style={filterStyles.filterSelect}
                                    disabled={isLoadingFilterOptions}
                                >
                                    <option value="">All Regions</option>
                                    {isLoadingFilterOptions ? (
                                        <option disabled>Loading regions...</option>
                                    ) : (
                                        getFilterOptions('regions', uniqueRegions).map(region => (
                                            <option key={region} value={region}>{region}</option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Sub Region</label>
                                <select
                                    value={draftSubRegion}
                                    onChange={(e) => setDraftSubRegion(e.target.value)}
                                    style={filterStyles.filterSelect}
                                    disabled={isLoadingFilterOptions}
                                >
                                    <option value="">All Sub Regions</option>
                                    {isLoadingFilterOptions ? (
                                        <option disabled>Loading sub regions...</option>
                                    ) : (
                                        getFilterOptions('subRegions', uniqueSubRegions).map(subRegion => (
                                            <option key={subRegion} value={subRegion}>{subRegion}</option>
                                        ))
                                    )}
                                </select>
                            </div>
                        </div>

                        {/* Special Filters */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            marginTop: '16px',
                            marginBottom: '12px'
                        }}
                        onClick={() => toggleSection('specialFilters')}>
                            <span style={{ flex: 1 }}>Special Filters</span>
                            {collapsedSections.specialFilters ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.specialFilters ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <div style={filterStyles.checkboxGroup}>
                                    <label
                                        style={{
                                            ...filterStyles.checkbox,
                                            ...(draftHasLwin === true ? filterStyles.checkboxChecked : {})
                                        }}
                                        onClick={() => setDraftHasLwin(draftHasLwin === true ? undefined : true)}
                                    >
                                        {draftHasLwin === true ? <CheckSquare size={16} /> : <div style={{ width: 16, height: 16, border: '1px solid var(--border-strong)', borderRadius: '3px' }}></div>}
                                        Has LWIN
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Vintage Filters */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            marginTop: '16px',
                            marginBottom: '12px'
                        }}
                        onClick={() => toggleSection('vintageFilters')}>
                            <span style={{ flex: 1 }}>Vintage Filters</span>
                            {collapsedSections.vintageFilters ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.vintageFilters ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Maturity</label>
                                <select
                                    value={draftMaturity}
                                    onChange={(e) => setDraftMaturity(e.target.value)}
                                    style={filterStyles.filterSelect}
                                >
                                    <option value="">All Maturities</option>
                                    <option value="UNKNOWN">Unknown</option>
                                    <option value="NOT_READY">Not Ready</option>
                                    <option value="READY_YOUNG">Ready - Youthful</option>
                                    <option value="READY_AT_BEST">Ready - At Best</option>
                                    <option value="READY_MATURE">Ready - Mature</option>
                                </select>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Drinking Window</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        placeholder="From Year"
                                        value={draftDrinkingFromYear ?? ''}
                                        onChange={(e) => setDraftDrinkingFromYear(e.target.value ? parseInt(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, flex: 1 }}
                                        min="1900"
                                        max="2100"
                                    />
                                    <span style={{ color: 'var(--ink-500)', fontSize: '14px' }}>to</span>
                                    <input
                                        type="number"
                                        placeholder="To Year"
                                        value={draftDrinkingToYear ?? ''}
                                        onChange={(e) => setDraftDrinkingToYear(e.target.value ? parseInt(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, flex: 1 }}
                                        min="1900"
                                        max="2100"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Critic Reviews */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            marginTop: '16px',
                            marginBottom: '12px'
                        }}
                        onClick={() => toggleSection('criticReviews')}>
                            <span style={{ flex: 1 }}>Critic Reviews</span>
                            {collapsedSections.criticReviews ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.criticReviews ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <div style={filterStyles.checkboxGroup}>
                                    <label
                                        style={{
                                            ...filterStyles.checkbox,
                                            ...(draftHasCriticReviews === true ? filterStyles.checkboxChecked : {})
                                        }}
                                        onClick={() => setDraftHasCriticReviews(draftHasCriticReviews === true ? undefined : true)}
                                    >
                                        {draftHasCriticReviews === true ? <CheckSquare size={16} /> : <div style={{ width: 16, height: 16, border: '1px solid var(--border-strong)', borderRadius: '3px' }}></div>}
                                        Has Critic Reviews
                                    </label>
                                </div>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Score Range (normalized to 0-100)</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        placeholder="Min Score"
                                        value={draftCriticScoreMin ?? ''}
                                        onChange={(e) => setDraftCriticScoreMin(e.target.value ? parseFloat(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, flex: 1 }}
                                        min="0"
                                        max="100"
                                        step="0.1"
                                    />
                                    <span style={{ color: 'var(--ink-500)', fontSize: '14px' }}>to</span>
                                    <input
                                        type="number"
                                        placeholder="Max Score"
                                        value={draftCriticScoreMax ?? ''}
                                        onChange={(e) => setDraftCriticScoreMax(e.target.value ? parseFloat(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, flex: 1 }}
                                        min="0"
                                        max="100"
                                        step="0.1"
                                    />
                                </div>
                                <div style={{ fontSize: '12px', color: 'var(--ink-500)', marginTop: '4px' }}>
                                    Scores /20 converted: (score × 2.5) + 50
                                </div>
                            </div>
                        </div>

                        {/* Date Filters */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            marginTop: '16px',
                            marginBottom: '12px'
                        }}
                        onClick={() => toggleSection('dateFilters')}>
                            <span style={{ flex: 1 }}>Date Filters</span>
                            {collapsedSections.dateFilters ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.dateFilters ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Modified After</label>
                                <input
                                    type="date"
                                    value={draftModifiedAfter}
                                    onChange={(e) => setDraftModifiedAfter(e.target.value)}
                                    style={filterStyles.filterInput}
                                />
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Modified Before</label>
                                <input
                                    type="date"
                                    value={draftModifiedBefore}
                                    onChange={(e) => setDraftModifiedBefore(e.target.value)}
                                    style={filterStyles.filterInput}
                                />
                            </div>
                        </div>

                        {/* Price Comparison Filters */}
                        <div style={{
                            ...filterStyles.filterSectionTitle,
                            marginTop: '16px',
                            marginBottom: '12px'
                        }}
                        onClick={() => toggleSection('priceComparison')}>
                            <span style={{ flex: 1 }}>Price Comparison</span>
                            {collapsedSections.priceComparison ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </div>

                        <div style={{
                            ...filterStyles.filterSectionContent,
                            ...(collapsedSections.priceComparison ? filterStyles.filterSectionCollapsed : filterStyles.filterSectionExpanded)
                        }}>
                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Price vs Market (%)</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        placeholder="Min %"
                                        value={draftPriceToMarketPercentageMin ?? ''}
                                        onChange={(e) => setDraftPriceToMarketPercentageMin(e.target.value ? parseFloat(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, flex: 1 }}
                                    />
                                    <span style={{ color: 'var(--ink-500)', fontSize: '14px' }}>to</span>
                                    <input
                                        type="number"
                                        placeholder="Max %"
                                        value={draftPriceToMarketPercentageMax ?? ''}
                                        onChange={(e) => setDraftPriceToMarketPercentageMax(e.target.value ? parseFloat(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, flex: 1 }}
                                    />
                                </div>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Price vs Wine Searcher (%)</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        placeholder="Min %"
                                        value={draftPriceToWineSearcherPercentageMin ?? ''}
                                        onChange={(e) => setDraftPriceToWineSearcherPercentageMin(e.target.value ? parseFloat(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, flex: 1 }}
                                    />
                                    <span style={{ color: 'var(--ink-500)', fontSize: '14px' }}>to</span>
                                    <input
                                        type="number"
                                        placeholder="Max %"
                                        value={draftPriceToWineSearcherPercentageMax ?? ''}
                                        onChange={(e) => setDraftPriceToWineSearcherPercentageMax(e.target.value ? parseFloat(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, flex: 1 }}
                                    />
                                </div>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Price vs Last Transaction (%)</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        placeholder="Min %"
                                        value={draftPriceToLastTransactionPercentageMin ?? ''}
                                        onChange={(e) => setDraftPriceToLastTransactionPercentageMin(e.target.value ? parseFloat(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, flex: 1 }}
                                    />
                                    <span style={{ color: 'var(--ink-500)', fontSize: '14px' }}>to</span>
                                    <input
                                        type="number"
                                        placeholder="Max %"
                                        value={draftPriceToLastTransactionPercentageMax ?? ''}
                                        onChange={(e) => setDraftPriceToLastTransactionPercentageMax(e.target.value ? parseFloat(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, flex: 1 }}
                                    />
                                </div>
                            </div>

                            <div style={filterStyles.filterGroup}>
                                <label style={filterStyles.filterLabel}>Market Price vs Highest Bid (%)</label>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        placeholder="Min %"
                                        value={draftPriceToHighestBidPercentageMin ?? ''}
                                        onChange={(e) => setDraftPriceToHighestBidPercentageMin(e.target.value ? parseFloat(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, flex: 1 }}
                                    />
                                    <span style={{ color: 'var(--ink-500)', fontSize: '14px' }}>to</span>
                                    <input
                                        type="number"
                                        placeholder="Max %"
                                        value={draftPriceToHighestBidPercentageMax ?? ''}
                                        onChange={(e) => setDraftPriceToHighestBidPercentageMax(e.target.value ? parseFloat(e.target.value) : undefined)}
                                        style={{ ...filterStyles.filterInput, flex: 1 }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Filter Actions - Apply and Clear buttons */}
                        <div style={filterStyles.filterActions}>
                            <button
                                style={filterStyles.applyButton}
                                onClick={applyAllFilters}
                                onMouseEnter={(e) => Object.assign(e.currentTarget.style, filterStyles.applyButtonHover)}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = filterStyles.applyButton.backgroundColor;
                                }}
                            >
                                <Search size={14} />
                                Apply Filters
                            </button>

                            <button
                                style={filterStyles.filterClearButton}
                                onClick={clearFilters}
                            >
                                <X size={14} />
                                Clear All
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Content - Product List */}
                <div style={styles.mainContent}>
                    {focusVintageId && (
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                            padding: '10px 14px', marginBottom: '16px',
                            backgroundColor: 'var(--info-bg)', border: '1px solid var(--info-border)',
                            borderRadius: radius.md, fontSize: '13px', color: 'var(--ink-700)',
                        }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Filter size={14} />
                                Showing a single linked vintage{vintages.length === 0 ? ' (not found)' : ''}.
                            </span>
                            <button
                                onClick={() => setFocusVintageId('')}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                    border: '1px solid var(--info-border)', background: 'transparent',
                                    color: 'var(--ink-700)', borderRadius: radius.sm, padding: '4px 10px',
                                    cursor: 'pointer', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap',
                                }}
                            >
                                <X size={12} /> Clear
                            </button>
                        </div>
                    )}
                    {/* Flat vintage list — each vintage is a first-class card carrying its product summary */}
                    {vintages.map((vintage: any) => {
                                const product = vintage.product || {};
                                const wineIconColor = getWineIconColor(product.colour || '');
                                // Distinct sources contributing listings to this vintage (at-a-glance link presence).
                                const vintageSources: string[] = Array.from(new Set(
                                    (vintage.productVintageSkus || []).flatMap((s: any) =>
                                        (s.productVintageSkuListings || []).map((l: any) => l.sourceCode))
                                )).filter(Boolean) as string[];
                                return (
                                                    <div key={vintage.masterProductVintageId} style={styles.vintageCard}>
                                                        <div style={styles.vintageHeader}>
                                                            <div style={styles.vintageContent}>
                                                                <div style={styles.vintageLeft}>
                                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                        {/* Title row — vintage + product name (BBR-style); LWIN sits here, wine icon with maturity below */}
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' as const }}>
                                                                            {vintage.sourceUrl ? (
                                                                                <a
                                                                                    href={vintage.sourceUrl}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    style={{
                                                                                        ...styles.vintageTitle,
                                                                                        color: 'var(--claret-600)',
                                                                                        textDecoration: 'underline',
                                                                                        cursor: 'pointer'
                                                                                    }}
                                                                                >
                                                                                    {formatVintage(vintage.vintage)} {product.name}
                                                                                </a>
                                                                            ) : (
                                                                                <h3 style={styles.vintageTitle}>
                                                                                    {formatVintage(vintage.vintage)} {product.name}
                                                                                </h3>
                                                                            )}
                                                                            {(product as any).lwin && (
                                                                                <span style={styles.lwinBadge}>LWIN {(product as any).lwin}</span>
                                                                            )}
                                                                            {/* Which sources have a listing for this vintage (at-a-glance link presence) */}
                                                                            {vintageSources.map(code => (
                                                                                <span key={code} style={sourceBadgeStyle(code)} title={`Has a ${sourceLabel(code)} listing`}>
                                                                                    {sourceLabel(code)}
                                                                                </span>
                                                                            ))}
                                                                            {product.bbrOwnSelection && (
                                                                                <span style={styles.bbrBadge}>BBR Selection</span>
                                                                            )}
                                                                            {(product as any).lwin && (
                                                                                <LwinChain lwin={(product as any).lwin} />
                                                                            )}
                                                                            <CopyableIds ids={[
                                                                                { label: 'Product ID', value: product.masterProductId },
                                                                                { label: 'Vintage ID', value: vintage.masterProductVintageId },
                                                                            ]} />
                                                                        </div>

                                                                        <div style={{
                                                                                fontSize: '13px',
                                                                                color: 'var(--ink-500)',
                                                                                marginTop: '8px',
                                                                                display: 'flex',
                                                                                gap: '14px',
                                                                                alignItems: 'center'
                                                                            }}>
                                                                                <Wine size={18} color={wineIconColor} style={{ flexShrink: 0 }} />
                                                                                {vintage.maturity && (
                                                                                    <span style={{ fontStyle: 'italic' }}>Maturity: {formatMaturity(vintage.maturity)}</span>
                                                                                )}
                                                                                {(vintage.drinkingFromYear || vintage.drinkingToYear) && (
                                                                                    <span style={{ fontStyle: 'italic' }}>
                                                                                        Drinking Window: {vintage.drinkingFromYear || 'N/A'} - {vintage.drinkingToYear || 'N/A'}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        <CriticReviewsList
                                                                            inline
                                                                            reviews={((vintage.criticReviews as MasterProductVintageCriticReview[] | undefined) || []).map((review) => ({
                                                                                reviewId: review.reviewId,
                                                                                criticName: review.wineCritic?.name,
                                                                                criticSource: review.wineCritic?.source,
                                                                                score: review.score,
                                                                                reviewDate: review.reviewDate,
                                                                                reviewText: review.reviewText,
                                                                            }))}
                                                                            onSelect={setSelectedReview}
                                                                        />

                                                                        {/* Cellar status badges - now shown at SKU level */}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* SKUs and Listings */}
                                                        <div style={styles.listingsContainer}>
                                                            {vintage.productVintageSkus &&
                                                                (vintage.productVintageSkus as unknown as MasterProductVintageSku[]).map((sku, skuIndex) => (
                                                                    <div
                                                                        key={sku.masterSkuId}
                                                                        style={{ ...styles.skuContainer, ...(skuIndex > 0 ? { borderTop: `1px solid var(--border)` } : {}) }}
                                                                    >
                                                                        {/* SKU Header with aggregate pricing data */}
                                                                        <div style={styles.skuHeader}>
                                                                            <div style={{
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                alignItems: 'center',
                                                                                width: '100%',
                                                                                gap: '16px'
                                                                            }}>
                                                                                {/* Left Section — size only; technical IDs tucked behind the copy icon */}
                                                                                <div style={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: '4px',
                                                                                    flexShrink: 0,
                                                                                    whiteSpace: 'nowrap' as const,
                                                                                }}>
                                                                                    <h4 style={styles.skuTitle}>
                                                                                        {sku.bottleSizeMl}ml × {sku.caseSize} case
                                                                                    </h4>
                                                                                    <CopyableIds ids={[
                                                                                        { label: 'SKU UID', value: sku.masterSkuId },
                                                                                        { label: 'SKU code', value: sku.canonicalSkuCode },
                                                                                        { label: 'LWIN16', value: sku.lwin16 },
                                                                                    ]} />
                                                                                </div>

                                                                                {/* Right Section - Pricing Grid (shared component) */}
                                                                                <ReferencePriceGrid
                                                                                    style={{ marginTop: '0', flex: 1, minWidth: '750px' }}
                                                                                    rows={[
                                                                                        { label: 'Market Price', price: sku.adjustedMarketPrice, date: sku.adjustedMarketPriceUpdateDate },
                                                                                        { label: 'Last Transaction', price: sku.lastTransaction, date: sku.lastTransactionUpdateDate },
                                                                                        { label: 'Highest Bid', price: sku.highestBid, date: sku.highestBidUpdateDate },
                                                                                        { label: 'Lowest Listing', price: sku.leastListingPrice, date: sku.leastListingPriceUpdateDate },
                                                                                        { label: 'Wine Searcher', price: sku.wineSearcherPrice, date: sku.wineSearcherPriceUpdateDate },
                                                                                    ]}
                                                                                />
                                                                            </div>
                                                                        </div>

                                                                        {/* My Account Status for this SKU (only show if we have price data) */}
                                                                        {sku.masterSkuId && skuStatus[sku.masterSkuId] && (() => {
                                                                            const ss = skuStatus[sku.masterSkuId];
                                                                            const hasData = ss.purchasePrice != null || ss.bbxListingPrice != null || ss.highestBidReceived != null || ss.cellarMarketPrice != null || ss.hasBidsPlaced;
                                                                            if (!hasData) return null;
                                                                            return (
                                                                                <div style={{
                                                                                    marginTop: '12px',
                                                                                    padding: '10px 14px',
                                                                                    backgroundColor: 'var(--info-bg)',
                                                                                    border: '1px solid var(--info-border)',
                                                                                    borderRadius: '8px',
                                                                                    fontSize: '13px'
                                                                                }}>
                                                                                    <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--info)', fontSize: '12px' }}>
                                                                                        📦 My Account
                                                                                    </div>
                                                                                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                                                        {ss.inCellar && (() => {
                                                                                            const cases = ss.totalCases ?? ss.cellarItemCount;
                                                                                            return (
                                                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                                                    <span style={{ fontSize: '11px', color: 'var(--ink-500)' }}>In Cellar</span>
                                                                                                    <span style={{ fontWeight: 600, color: 'var(--info)' }}>
                                                                                                        {cases} {cases === 1 ? 'case' : 'cases'}
                                                                                                    </span>
                                                                                                </div>
                                                                                            );
                                                                                        })()}
                                                                                        {Array.isArray(ss.holdings) && ss.holdings.length > 0 ? (
                                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                                                <span style={{ fontSize: '11px', color: 'var(--ink-500)' }}>
                                                                                                    {ss.holdings.length === 1 ? 'Purchase Price' : `Holdings · paid (${ss.holdings.length})`}
                                                                                                </span>
                                                                                                {ss.holdings.map((h: any, i: number) => (
                                                                                                    <span key={i} style={{ fontWeight: 600, color: 'var(--info)' }}>
                                                                                                        {h.cases} {h.cases === 1 ? 'case' : 'cases'} · {h.purchasePrice != null ? `£${Number(h.purchasePrice).toFixed(0)}` : 'price n/a'}
                                                                                                    </span>
                                                                                                ))}
                                                                                            </div>
                                                                                        ) : ss.purchasePrice != null && (
                                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                                                <span style={{ fontSize: '11px', color: 'var(--ink-500)' }}>Purchase Price</span>
                                                                                                <span style={{ fontWeight: 600, color: 'var(--info)' }}>
                                                                                                    £{Number(ss.purchasePrice).toFixed(0)}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}
                                                                                        {ss.bbxListingPrice != null && (
                                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                                                <span style={{ fontSize: '11px', color: 'var(--ink-500)' }}>My Listing Price</span>
                                                                                                <span style={{ fontWeight: 600, color: 'var(--success)' }}>
                                                                                                    £{Number(ss.bbxListingPrice).toFixed(0)}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}
                                                                                        {ss.highestBidReceived != null && (
                                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                                                <span style={{ fontSize: '11px', color: 'var(--ink-500)' }}>Highest Bid Received</span>
                                                                                                <span style={{ fontWeight: 600, color: '#854d0e' }}>
                                                                                                    £{Number(ss.highestBidReceived).toFixed(0)}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}
                                                                                        {ss.cellarMarketPrice != null && (
                                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                                                <span style={{ fontSize: '11px', color: 'var(--ink-500)' }}>Market Value</span>
                                                                                                <span style={{ fontWeight: 600, color: 'var(--ink-700)' }}>
                                                                                                    £{Number(ss.cellarMarketPrice).toFixed(0)}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}
                                                                                        {ss.changeValue != null && (
                                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                                                <span style={{ fontSize: '11px', color: 'var(--ink-500)' }}>P&L</span>
                                                                                                <span style={{ fontWeight: 600, color: Number(ss.changeValue) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                                                                                                    {Number(ss.changeValue) >= 0 ? '+' : ''}£{Number(ss.changeValue).toFixed(0)}
                                                                                                    {ss.changeValuePercentage != null && (
                                                                                                        <span style={{ fontSize: '11px', fontWeight: 400 }}>
                                                                                                            {' '}({Number(ss.changeValuePercentage) >= 0 ? '+' : ''}{Number(ss.changeValuePercentage).toFixed(1)}%)
                                                                                                        </span>
                                                                                                    )}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}
                                                                                        {ss.hasBidsPlaced && (
                                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                                                                <span style={{ fontSize: '11px', color: 'var(--ink-500)' }}>My Bids Placed</span>
                                                                                                <span style={{ fontWeight: 600, color: '#5b21b6' }}>
                                                                                                    {ss.bidsPlacedCount} {ss.bidsPlacedCount === 1 ? 'bid' : 'bids'}
                                                                                                    {ss.lowestBidPlaced != null ? ` · £${Number(ss.lowestBidPlaced).toFixed(0)}` : ''}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })()}

                                                                        {/* Bid Opportunity Indicator — slim, no fill (de-emphasised) */}
                                                                        {sku.productVintageSkuListings && (sku.productVintageSkuListings as unknown as MasterProductVintageSkuListing[]).length > 0 && (
                                                                            <div style={{
                                                                                padding: '8px 20px',
                                                                                backgroundColor: 'transparent',
                                                                                borderBottom: `1px solid ${color.border}`,
                                                                                fontSize: '12px',
                                                                                color: 'var(--ink-500)'
                                                                            }}>
                                                                                {(() => {
                                                                                    const firstListing = (sku.productVintageSkuListings as unknown as MasterProductVintageSkuListing[])[0];
                                                                                    const bidPercentage = firstListing.highestBidToMarketPercentage;

                                                                                    if (bidPercentage === null || bidPercentage === undefined) {
                                                                                        return (
                                                                                            <span style={{ color: 'var(--ink-500)' }}>
                                                                                                <strong>Bidding Opportunity:</strong> No bids yet - market value is {sku.adjustedMarketPrice ? `£${sku.adjustedMarketPrice.toLocaleString()}` : 'N/A'}
                                                                                            </span>
                                                                                        );
                                                                                    }

                                                                                    const getColor = (pct: number) => {
                                                                                        if (pct < -20) return 'var(--success)';
                                                                                        if (pct < -10) return 'var(--warning)';
                                                                                        return 'var(--danger)';
                                                                                    };

                                                                                    const getLabel = (pct: number) => {
                                                                                        if (pct < -20) return 'Strong Opportunity';
                                                                                        if (pct < -10) return 'Moderate Opportunity';
                                                                                        return 'Limited Opportunity';
                                                                                    };

                                                                                    return (
                                                                                        <>
                                                                                            <strong>Bidding Opportunity:</strong>{' '}
                                                                                            <span style={{ color: getColor(bidPercentage), fontWeight: 'bold' }}>
                                                                                                {getLabel(bidPercentage)} ({formatPercentage(bidPercentage)})
                                                                                            </span>
                                                                                            {' - '}Highest bid ({sku.highestBid ? `£${sku.highestBid.toLocaleString()}` : 'N/A'}) is{' '}
                                                                                            <span style={{ color: getColor(bidPercentage), fontWeight: 'bold' }}>
                                                                                                {Math.abs(bidPercentage).toFixed(1)}% below
                                                                                            </span>
                                                                                            {' '}market value ({sku.adjustedMarketPrice ? `£${sku.adjustedMarketPrice.toLocaleString()}` : 'N/A'})
                                                                                            <span style={{ color: 'var(--ink-500)', fontSize: '12px', marginLeft: '8px' }}>
                                                                                                • Min bid: {sku.adjustedMarketPrice ? `£${(sku.adjustedMarketPrice * 0.7).toLocaleString()}` : 'N/A'} (-30%)
                                                                                            </span>
                                                                                        </>
                                                                                    );
                                                                                })()}
                                                                            </div>
                                                                        )}

                                                                        {/* Listings Section */}
                                                                        {sku.productVintageSkuListings &&
                                                                            (sku.productVintageSkuListings as unknown as MasterProductVintageSkuListing[]).length > 0 && (
                                                                                <div style={styles.listingsSection}>
                                                                                    {/* Column header (BBR-style) — labels the shared grid tracks once per SKU */}
                                                                                    <div style={styles.listingHeaderRow}>
                                                                                        <div style={styles.listingColLabel}>Price / Case</div>
                                                                                        <div style={styles.listingColLabel}>Stock</div>
                                                                                        <div style={styles.listingColLabel}>Available</div>
                                                                                        <div style={styles.listingColLabel}>Comparisons</div>
                                                                                        <div style={{ ...styles.listingColLabel, textAlign: 'right' as const }}>IDs</div>
                                                                                    </div>
                                                                                    {(sku.productVintageSkuListings as unknown as MasterProductVintageSkuListing[]).map((listing, listingIndex) => {
                                                                                        return (
                                                                                            <div
                                                                                                key={listing.skuListingId || listingIndex}
                                                                                                style={styles.listingRow}
                                                                                            >
                                                                                                {/* Col 1 — Price + source badge (the badge links to that merchant's listing) */}
                                                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'flex-start' }}>
                                                                                                    <div style={styles.listingPrice}>
                                                                                                        £{listing.pricePerCase?.toLocaleString() || 'N/A'}
                                                                                                    </div>
                                                                                                    {(listing as any).sourceUrl ? (
                                                                                                        <a
                                                                                                            href={(listing as any).sourceUrl}
                                                                                                            target="_blank"
                                                                                                            rel="noopener noreferrer"
                                                                                                            style={sourceBadgeStyle(listing.sourceCode)}
                                                                                                            title={`View on ${sourceLabel(listing.sourceCode)}`}
                                                                                                        >
                                                                                                            {sourceLabel(listing.sourceCode)} ↗
                                                                                                        </a>
                                                                                                    ) : (
                                                                                                        <span style={sourceBadgeStyle(listing.sourceCode)}>
                                                                                                            {sourceLabel(listing.sourceCode)}
                                                                                                        </span>
                                                                                                    )}
                                                                                                </div>

                                                                                                {/* Col 2 — Stock origin / purchase mode */}
                                                                                                <div style={{ ...styles.listingCell, fontSize: '13px', color: 'var(--ink-600)' }}>
                                                                                                    {listing.stockOrigin && (
                                                                                                        <span>{listing.stockOrigin}</span>
                                                                                                    )}
                                                                                                    {listing.purchaseMode && (
                                                                                                        <span>{listing.purchaseMode}</span>
                                                                                                    )}
                                                                                                </div>

                                                                                                {/* Col 3 — Availability */}
                                                                                                <div style={{ fontSize: '13px', color: 'var(--ink-600)' }}>
                                                                                                    {listing.availableCases && listing.availableCases > 0 ? `${listing.availableCases} ${listing.availableCases === 1 ? 'case' : 'cases'}` : '—'}
                                                                                                </div>

                                                                                                {/* Col 4 — Price comparisons */}
                                                                                                <PriceComparisons
                                                                                                    style={{ marginTop: 0, padding: 0, backgroundColor: 'transparent', border: 'none', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
                                                                                                    rows={[
                                                                                                        { label: 'vs Market:', percentage: listing.priceToMarketPercentage },
                                                                                                        { label: 'vs Wine Searcher:', percentage: listing.priceToWineSearcherPercentage },
                                                                                                        { label: 'vs Last Trans:', percentage: listing.priceToLastTransactionPercentage },
                                                                                                    ]}
                                                                                                />

                                                                                                {/* Col 5 — Listing IDs (tucked behind copy icon) */}
                                                                                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                                                    <CopyableIds ids={[
                                                                                                        { label: 'Listing ID', value: listing.skuListingId },
                                                                                                        { label: 'Source', value: sourceLabel(listing.sourceCode) },
                                                                                                        { label: 'External ID', value: listing.externalListingId },
                                                                                                        // BBR-specific ids (null for other sources → CopyableIds skips them)
                                                                                                        { label: 'BBX', value: listing.bbxListingId },
                                                                                                        { label: 'Hybris', value: listing.hybrisBbxListingId },
                                                                                                    ]} />
                                                                                                </div>

                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            )}
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    </div>
                                                );
                    })}

                    {/* Pagination */}
                    <div style={styles.pagination}>
                        <button
                            style={{
                                ...styles.pageButton,
                                opacity: page === 0 ? 0.5 : 1,
                                cursor: page === 0 ? 'not-allowed' : 'pointer'
                            }}
                            onClick={() => page > 0 && setPage(page - 1)}
                            disabled={page === 0}
                        >
                            Previous
                        </button>

                        <div style={styles.pageInfo}>
                            Page {page + 1} of {totalPages}
                        </div>

                        <button
                            style={{
                                ...styles.pageButton,
                                opacity: page >= totalPages - 1 ? 0.5 : 1,
                                cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer'
                            }}
                            onClick={() => page < totalPages - 1 && setPage(page + 1)}
                            disabled={page >= totalPages - 1}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>

            {/* Critic Review Modal (shared component) */}
            <CriticReviewModal review={selectedReview} onClose={() => setSelectedReview(null)} />
        </div>
    );
};

export default MasterProducts;

